import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
} from "recharts";
import AxiosInstance from "../../Config/AxiosInstance";
import { ApiList } from "../../Config/ApiList";

// Human-readable labels for the paywall surface identifiers the backend
// returns. Keep in sync with PAYWALL_SURFACES in
// node-treatwalk-API-main/controller/Metrics/c_metrics.js.
const SURFACE_LABELS = {
  onboarding_paywall:        "Onboarding paywall (S19)",
  settings_subscription_link: "Settings → Subscription",
  super_boop_home:           "Super Boop — Home feed",
  super_boop_other_profile:  "Super Boop — Other profile",
  super_boop_feed_list:      "Super Boop — Profile feed",
  rings_locked_preview:      "Activity Rings — locked preview",
  rings_detail_gated:        "Activity Rings — detail gate",
  progress_insights_locked:  "Progress insights — locked",
  dog_sharing_locked:        "Dog sharing — locked",
  streak_restore:            "Streak restore (0 left)",
  restore_no_plan_found:     "Restore — no plan found",
  require_premium_gate:      "Generic premium gate",
};

const RANGE_OPTIONS = [
  { label: "Last 4 weeks",   value: 4  },
  { label: "Last 13 weeks (quarter)", value: 13 },
  { label: "Last 26 weeks",  value: 26 },
  { label: "Last 52 weeks",  value: 52 },
];

function StatCard({ label, value, suffix }) {
  return (
    <Box sx={{
      flex: 1,
      minWidth: 200,
      p: 3,
      borderRadius: 3,
      backgroundColor: "#fff",
      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      border: "1px solid #f0f0f0",
    }}>
      <Typography variant="body2" sx={{ color: "#666", mb: 1 }}>{label}</Typography>
      <Typography variant="h4" sx={{ fontWeight: 600 }}>
        {value}
        {suffix && (
          <Typography component="span" variant="h6" sx={{ ml: 0.5, color: "#666" }}>
            {suffix}
          </Typography>
        )}
      </Typography>
    </Box>
  );
}

function ChartCard({ title, children }) {
  return (
    <Box sx={{
      p: 3,
      borderRadius: 3,
      backgroundColor: "#fff",
      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      border: "1px solid #f0f0f0",
    }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>{title}</Typography>
      <Box sx={{ width: "100%", height: 320 }}>
        <ResponsiveContainer>{children}</ResponsiveContainer>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------- Walks tab
function WalksTab({ weeks }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    AxiosInstance.get(`${ApiList.METRICS_WALKS}?weeks=${weeks}`)
      .then((res) => {
        if (cancelled) return;
        if (res?.data?.s === 1) {
          setData(res.data.r);
        } else {
          setError(res?.data?.m || "Failed to load walk metrics");
        }
      })
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [weeks]);

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>;
  if (error) return <Typography sx={{ color: "error.main", p: 2 }}>{error}</Typography>;
  if (!data) return null;

  const chartData = (data.trend || []).map(t => ({
    week: t.week_start ? new Date(t.week_start).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : t.period,
    Walks: t.walks,
    "Active walkers": t.users,
  }));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
        <StatCard label="This week — walks completed" value={data.week.walks} />
        <StatCard label="This week — active walkers" value={data.week.users} />
      </Box>

      <ChartCard title="Weekly trend">
        <LineChart data={chartData} margin={{ top: 10, right: 24, left: 0, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
          <XAxis dataKey="week" tick={{ fontSize: 12 }} />
          <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
          <Tooltip />
          <Legend />
          <Line type="monotone" dataKey="Walks"           stroke="#b9cc03" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="Active walkers"  stroke="#3f88c5" strokeWidth={2} dot={false} />
        </LineChart>
      </ChartCard>
    </Box>
  );
}

// ---------------------------------------------------------------- Paywall tab
function PaywallTab({ weeks }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    AxiosInstance.get(`${ApiList.METRICS_PAYWALL}?weeks=${weeks}`)
      .then((res) => {
        if (cancelled) return;
        if (res?.data?.s === 1) {
          setData(res.data.r);
        } else {
          setError(res?.data?.m || "Failed to load paywall metrics");
        }
      })
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [weeks]);

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>;
  if (error) return <Typography sx={{ color: "error.main", p: 2 }}>{error}</Typography>;
  if (!data) return null;

  const trendData = (data.trend || []).map(t => ({
    week: t.week_start ? new Date(t.week_start).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : t.period,
    "Conversion %": t.conversion_pct,
    "Viewers":      t.viewers,
    "Converted":    t.converted,
  }));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Reach — engagement signal */}
      <Box>
        <Typography variant="overline" sx={{ color: "#5b6e00", fontWeight: 700, letterSpacing: 1 }}>
          Reach · who's seeing paywalls
        </Typography>
        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mt: 1 }}>
          <StatCard label="30-day active users (completed a walk)" value={data.actives} />
          <StatCard label="Active users who saw a paywall"          value={data.active_viewers} />
          <StatCard label="% of actives who saw a paywall"          value={data.reach_pct} suffix="%" />
        </Box>
      </Box>

      {/* Conversion — revenue signal */}
      <Box>
        <Typography variant="overline" sx={{ color: "#5b6e00", fontWeight: 700, letterSpacing: 1 }}>
          Conversion · who's buying after seeing a paywall
        </Typography>
        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mt: 1 }}>
          <StatCard label="Paywall viewers (last 30 days)" value={data.viewers} />
          <StatCard label="Converted to premium"           value={data.converted} />
          <StatCard label="Conversion rate"                value={data.conversion_pct} suffix="%" />
        </Box>
      </Box>

      <ChartCard title="Weekly conversion rate (of users who saw a paywall that week)">
        <LineChart data={trendData} margin={{ top: 10, right: 24, left: 0, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
          <XAxis dataKey="week" tick={{ fontSize: 12 }} />
          <YAxis tick={{ fontSize: 12 }} domain={[0, 100]} unit="%" />
          <Tooltip />
          <Legend />
          <Line type="monotone" dataKey="Conversion %" stroke="#b9cc03" strokeWidth={2} dot={false} />
        </LineChart>
      </ChartCard>

      <Box sx={{
        p: 3,
        borderRadius: 3,
        backgroundColor: "#fff",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        border: "1px solid #f0f0f0",
      }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Conversion by surface (last 30 days)
        </Typography>
        <Typography variant="caption" sx={{ color: "#888", display: "block", mb: 2 }}>
          A user counted as "converted" against every surface they saw before subscribing (multi-touch).
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 110px 110px 110px", rowGap: 1.2, columnGap: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#666" }}>Surface</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#666", textAlign: "right" }}>Viewers</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#666", textAlign: "right" }}>Converted</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#666", textAlign: "right" }}>Conversion %</Typography>
          {(data.by_surface || []).map((row) => (
            <React.Fragment key={row.surface}>
              <Typography variant="body2">
                {SURFACE_LABELS[row.surface] || row.surface}
              </Typography>
              <Typography variant="body2" sx={{ textAlign: "right" }}>{row.viewers}</Typography>
              <Typography variant="body2" sx={{ textAlign: "right" }}>{row.converted}</Typography>
              <Typography variant="body2" sx={{ textAlign: "right", color: row.viewers > 0 ? "#5b6e00" : "#aaa", fontWeight: row.viewers > 0 ? 600 : 400 }}>
                {row.viewers > 0 ? `${row.conversion_pct}%` : "—"}
              </Typography>
            </React.Fragment>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------- Page
const Metrics = () => {
  const [tab, setTab]     = useState(0);
  const [weeks, setWeeks] = useState(13);

  return (
    <Box sx={{ p: 3, backgroundColor: "#fafafa", minHeight: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Metrics</Typography>
        <Select
          size="small"
          value={weeks}
          onChange={(e) => setWeeks(Number(e.target.value))}
          sx={{ minWidth: 220, backgroundColor: "#fff" }}
        >
          {RANGE_OPTIONS.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </Select>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          mb: 3,
          "& .MuiTab-root": { textTransform: "none", fontWeight: 600 },
          "& .Mui-selected": { color: "#5b6e00 !important" },
          "& .MuiTabs-indicator": { backgroundColor: "#b9cc03" },
        }}
      >
        <Tab label="Walks" />
        <Tab label="Paywall" />
      </Tabs>

      {tab === 0 && <WalksTab weeks={weeks} />}
      {tab === 1 && <PaywallTab weeks={weeks} />}
    </Box>
  );
};

export default Metrics;
